"use client";

import {
    MessageScroller as MessageScrollerPrimitive,
    useMessageScroller,
    useMessageScrollerScrollable,
    useMessageScrollerVisibility,
} from "@shadcn/react/message-scroller";
import { ArrowDownIcon } from "lucide-react";
import type * as React from "react";
import { buttonVariants, type ButtonProps } from "./button";
import { cn } from "./utils";

// shadcn/ui Message Scroller (https://ui.shadcn.com/docs/components/base/message-scroller), ported to Tailwind 3.
// A scroll container for conversations and comment threads: it keeps the reader's place while content
// is added, and offers a jump-to-latest button.

function MessageScrollerProvider(props: React.ComponentProps<typeof MessageScrollerPrimitive.Provider>) {
    return <MessageScrollerPrimitive.Provider {...props} />;
}

function MessageScroller({ className, ...props }: React.ComponentProps<typeof MessageScrollerPrimitive.Root>) {
    return (
        <MessageScrollerPrimitive.Root
            data-slot="message-scroller"
            className={cn(
                "group/message-scroller relative flex flex-col w-full h-full min-h-0 overflow-hidden",
                className
            )}
            {...props}
        />
    );
}

function MessageScrollerViewport({
    className,
    ...props
}: React.ComponentProps<typeof MessageScrollerPrimitive.Viewport>) {
    return (
        <MessageScrollerPrimitive.Viewport
            data-slot="message-scroller-viewport"
            className={cn(
                "w-full h-full min-h-0 min-w-0 overflow-y-auto overscroll-contain [contain:content] data-[pending-scroll]:invisible",
                className
            )}
            {...props}
        />
    );
}

function MessageScrollerContent({
    className,
    ...props
}: React.ComponentProps<typeof MessageScrollerPrimitive.Content>) {
    return (
        <MessageScrollerPrimitive.Content
            data-slot="message-scroller-content"
            className={cn("flex flex-col h-max min-h-full", className)}
            {...props}
        />
    );
}

function MessageScrollerItem({
    className,
    scrollAnchor = false,
    ...props
}: React.ComponentProps<typeof MessageScrollerPrimitive.Item>) {
    return (
        <MessageScrollerPrimitive.Item
            data-slot="message-scroller-item"
            scrollAnchor={scrollAnchor}
            className={cn("min-w-0 shrink-0 [contain-intrinsic-size:auto_10rem] [content-visibility:auto]", className)}
            {...props}
        />
    );
}

function MessageScrollerButton({
    direction = "end",
    className,
    children,
    variant = "outline",
    size = "icon",
    ...props
}: React.ComponentProps<typeof MessageScrollerPrimitive.Button> & Pick<ButtonProps, "variant" | "size">) {
    return (
        <MessageScrollerPrimitive.Button
            data-slot="message-scroller-button"
            data-direction={direction}
            direction={direction}
            className={cn(
                buttonVariants({ variant, size }),
                "absolute left-1/2 -translate-x-1/2 shadow-sm transition-[transform,opacity] duration-200 data-[active=false]:pointer-events-none data-[active=false]:opacity-0 data-[direction=end]:bottom-4 data-[direction=start]:top-4 data-[direction=start]:[&_svg]:rotate-180",
                className
            )}
            {...props}
        >
            {children ?? (
                <>
                    <ArrowDownIcon className="size-4" aria-hidden="true" />
                    <span className="sr-only">{direction === "end" ? "Scroll to end" : "Scroll to start"}</span>
                </>
            )}
        </MessageScrollerPrimitive.Button>
    );
}

export {
    MessageScrollerProvider,
    MessageScroller,
    MessageScrollerViewport,
    MessageScrollerContent,
    MessageScrollerItem,
    MessageScrollerButton,
    useMessageScroller,
    useMessageScrollerScrollable,
    useMessageScrollerVisibility,
};
